import {Component, Mixins} from "vue-mixin-decorator";
import BetterSet from "betterset";
import {mapGetters} from "vuex";
import {Entities} from "@/store/Database";
import {HdsCollectionMap} from "@/store/modules/hds";
import {DataComponentModel, Dictionary, Scalars, ServiceComponentModel} from "@/store/modules/hds/Model";
import PathInfoMixin from "@/router/PathInfoMixin";
import ErrorMessage from "@/components/molecules/ErrorMessage";


interface DataComponentModelErrorMessage extends ErrorMessage {
  type: 'dataPropertyDefinition' | 'dataComponentModel';
}

interface ServiceComponentModelErrorMessage extends ErrorMessage {
  type: 'processComponentModel' | 'processOperationList';
}

/**
 * エラー情報を生成する mixin
 */
@Component({
  ...mapGetters({
    entities: 'hds/entities',
  }),
})
export default class ErrorBuilderMixin extends Mixins<PathInfoMixin>(PathInfoMixin) {
  /** すべての hds entity */
  private entities!: Entities<HdsCollectionMap>;

  /** 辞書*/
  private dictionary!: Dictionary;

  /**
   * データ構成モデルのエラーを生成します
   *
   * @param dcm データ構成モデル
   */
  buildDataComponentModelError(dcm: DataComponentModel): DataComponentModelErrorMessage[] {
    const errors: DataComponentModelErrorMessage[] = [];
    if(dcm.dataPropertyDefinitionIds?.length === 0) {
      errors.push({
        type: 'dataPropertyDefinition',
        level: 'error',
        message: 'データ項目定義が登録されていません'
      });
    }
    if(!this.isCategory2nd('common')) {
      const dtms = this.entities.dataTranslationMap.find({sourceDataComponentModelId: dcm.id});
      for (const rd of this.dictionary.referenceDictionaries ?? []) {
        const dtm = dtms.find(dtm => dtm.targetDictionaryId === rd.id && dtm.parentId === null);
        const dptms = dtms
            .filter(dtm => dtm.targetDictionaryId === rd.id)
            .flatMap(dtm => dtm.dataPropertyTranslationMaps);
        if (dtm) {
          const sourceDataPropertyDefinitionIds = dptms.map(dptm => dptm.sourceDataPropertyDefinition.id); // マップされた個別辞書側のデータ項目定義IDの配列
          if (new BetterSet(dcm.dataPropertyDefinitionIds).complement(new Set(sourceDataPropertyDefinitionIds)).size > 0) {
            errors.push({
              type: 'dataPropertyDefinition',
              level: 'warn',
              message: `共通辞書「${rd.name}」とマッピングが行われていないデータ項目定義が存在します`
            });
          }
          for(const dptm of dptms) {
            const sourceDpd = dptm.sourceDataPropertyDefinition;
            const targetDpd = dptm.targetDataPropertyDefinition;
            if(sourceDpd && sourceDpd.dataType !== targetDpd.dataType) {
              errors.push({
                type: 'dataPropertyDefinition',
                level: 'warn',
                message: `共通辞書「${rd.name}」とのマッピングにおいてデータ型が異なるデータ項目定義が存在します`
              });
              break;
            }
          }
        } else {
          errors.push({
            type: 'dataComponentModel',
            level: rd.id === this.dictionary.defaultDictionaryId ? 'warn' : 'info',
            message: `共通辞書「${rd.name}」とのマッピングが行われていません`
          });
        }
      }
    }
    return errors;
  }

  /**
   * サービス構成モデルのエラーを生成します
   *
   * @param scm サービス構成モデル
   */
  buildServiceComponentModelError(scm: ServiceComponentModel): ServiceComponentModelErrorMessage[] {
    const errors: ServiceComponentModelErrorMessage[] = [];

    // プロセス構成モデルのエラー
    if(scm.processComponentModelIds?.length === 0) {
      errors.push({
        type: 'processComponentModel',
        level: 'error',
        message: 'プロセス構成モデルが登録されていません'
      });
    } else {
      if(scm.processComponentModelIds?.some(pcmId =>
        this.entities.processOperation.count({processComponentModelId: pcmId}) === 0
      )) {
        errors.push({
          type: 'processComponentModel',
          level: 'warn',
          message: 'プロセス手順との紐付きのないプロセス構成モデルが存在します',
        });
      }
    }

    // プロセス手順の警告
    const poCount = this.entities.processOperation.find({
      serviceComponentModelId: scm.id,
    }).filter(po =>
      !po.dataComponentModelId || this.entities.dataComponentModel.count({id: po.dataComponentModelId}) === 0
    ).length;
    if(poCount) {
      errors.push({
        type: 'processOperationList',
        level: 'warn',
        message: 'プロセス手順に対象データ構成モデルが設定されていないものがあります'
      });
    }

    if(!this.isCategory2nd('common')) {
      const stms = this.entities.serviceTranslationMap.find({sourceServiceComponentModelId: scm.id});
      for (const rd of this.dictionary.referenceDictionaries ?? []) {
        const stm = stms.find(stm => stm.targetDictionaryId === rd.id);
        if (!stm) {
          errors.push({
            type: 'processOperationList',
            level: rd.id === this.dictionary.defaultDictionaryId ? 'warn' : 'info',
            message: `共通辞書「${rd.name}」とのマッピングが行われていません`
          });
        }
      }
    }
    return errors;
  }
}
